import { Badge, Box, Button, HStack, Separator, Spinner, Stack, Table, Text } from "@chakra-ui/react"
import { UI } from "components"
import { Mask } from "config"
import useMarketplaceOrders from "hooks/useMarketplaceOrders"
import { useState } from "react"
import Moment from "moment"

export default function MarketplaceOrders() {
    const [page, setPage] = useState(1)
    const { data, isLoading, isError } = useMarketplaceOrders(page)

    const orders      = data?.data ?? []
    const total       = data?.total ?? 0
    const lastPage    = data?.last_page ?? 1
    const perPage     = data?.per_page ?? 15

    const statusLabel = (status) => {
        const map = { pending: 'Pendente', paid: 'Pago', cancelled: 'Cancelado' }
        return map[status] ?? status
    }

    const statusColor = (status) => {
        const map = { pending: 'yellow', paid: 'green', cancelled: 'red' }
        return map[status] ?? 'gray'
    }

    return (
        <>
            <UI.Header title="Pedidos Marketplace" />

            {isError && (
                <Box bg="red.50" borderRadius={8} p={4} mb={4}>
                    <Text color="red.600" fontSize="sm">
                        Não foi possível carregar os pedidos marketplace. Verifique a integração.
                    </Text>
                </Box>
            )}

            <Stack gap={4}>
                <HStack justify="space-between">
                    <Text fontSize="sm" color="fg.muted">{total} pedido(s) encontrado(s)</Text>
                </HStack>

                <Table.Root size="sm" variant="outline" interactive>
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>Pedido</Table.ColumnHeader>
                            <Table.ColumnHeader>Itens</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right">Total</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right">Repasse</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="center">Status repasse</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right">Data</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isLoading && (
                            <Table.Row>
                                <Table.Cell colSpan={6}>
                                    <HStack justify="center" py={6}><Spinner size="sm" /></HStack>
                                </Table.Cell>
                            </Table.Row>
                        )}

                        {!isLoading && orders.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan={6}>
                                    <Text textAlign="center" color="fg.muted" py={6} fontSize="sm">
                                        Nenhum pedido marketplace encontrado.
                                    </Text>
                                </Table.Cell>
                            </Table.Row>
                        )}

                        {orders.map((order) => {
                            const receivable = order.nucleus_receivable
                            const itemCount  = order.items?.length ?? 0
                            return (
                                <Table.Row key={order.id}>
                                    <Table.Cell>
                                        <Text fontFamily="mono" fontSize="xs">{order.order_code}</Text>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Text fontSize="sm">{itemCount} item(ns)</Text>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <Text fontSize="sm" fontWeight="semibold">
                                            {Mask.price(order.total)}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        {receivable ? (
                                            <Stack gap={0} align="flex-end">
                                                <Text fontSize="sm" fontWeight="semibold" color="green.600">
                                                    {Mask.price(receivable.amount_to_nucleus)}
                                                </Text>
                                                <Text fontSize="2xs" color="fg.muted">
                                                    custo {Mask.price(receivable.cost_price)} + margem {Mask.price(receivable.margin)}
                                                </Text>
                                            </Stack>
                                        ) : (
                                            <Text fontSize="xs" color="fg.subtle">—</Text>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">
                                        {receivable ? (
                                            <Badge colorPalette={statusColor(receivable.status)} size="sm">
                                                {statusLabel(receivable.status)}
                                            </Badge>
                                        ) : (
                                            <Text fontSize="xs" color="fg.subtle">—</Text>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <Text fontSize="xs" color="fg.muted">
                                            {order.paid_at ? Moment(order.paid_at).format('DD/MM/YY HH:mm') : '—'}
                                        </Text>
                                    </Table.Cell>
                                </Table.Row>
                            )
                        })}
                    </Table.Body>
                </Table.Root>

                {lastPage > 1 && (
                    <>
                        <Separator />
                        <HStack justify="center" gap={2}>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                Anterior
                            </Button>
                            <Text fontSize="sm" color="fg.muted">{page} / {lastPage}</Text>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= lastPage}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Próxima
                            </Button>
                        </HStack>
                    </>
                )}
            </Stack>
        </>
    )
}
